/**
 * Stale-safe route prefetching.
 *
 * The pages already do stale-while-revalidate (staleTime + refetch-on-mount), so
 * the only spinner left is the *cold first visit*: the page mounts, then fires
 * its API call, then you wait the round-trip. This module fires that same call
 * a beat earlier — on hover/focus of a nav target (intent), and on idle for the
 * role's primary destinations — so the data is already in cache (or in-flight)
 * by the time the page mounts.
 *
 * Why this never shows stale data: every prefetch is a *fresh* network fetch
 * keyed identically to what the page will request (the queryOptions factories
 * are shared with the hooks). `prefetchQuery` respects staleTime, so a fresh
 * entry is reused and a stale one is refetched — never a saved snapshot. We
 * deliberately do NOT persist the cache across sessions; that's the only lever
 * with real stale-data risk.
 */
import type { QueryClient } from '@tanstack/react-query';
import { projectDetailOptions, projectsListOptions } from '@/api/projects';
import { dispatchDayOptions } from '@/api/dispatch';
import { scheduleWeekOptions } from '@/api/schedule';
import { crewOptions } from '@/api/crew';
import { activityFeedOptions } from '@/api/activity';
import { crewScheduleOptions, crewTodayOptions } from '@/api/crew-today';
import { mondayOfWeek, todayLocalISODate } from '@/lib/dates';

/**
 * Heavy, code-split routes worth warming the JS chunk for on intent — the chunk
 * download is itself a chunk of their perceived load. Specifiers must match
 * App.tsx's `lazy()` imports exactly so the bundler dedupes to the same chunk.
 */
const ROUTE_CHUNK: Record<string, () => Promise<unknown>> = {
  '/calendar': () => import('@/pages/ProjectCalendar'),
  '/insights': () => import('@/pages/Insights'),
};

/**
 * Warm a route's landing data (and JS chunk, if code-split). Fire-and-forget.
 *
 * Each branch prefetches the *exact* `queryOptions` the page will use — date-keyed
 * routes via the same shared date helpers the pages use, so the warmed key matches
 * by construction. `prefetchQuery` is called per-branch (not over a collected
 * array) so each call infers its own concrete query type.
 *
 * Calendar/Insights are intentionally data-less here: their windows are derived at
 * render (FullCalendar's visible range; an analytics date-picker), so there's no
 * stable key to warm ahead of mount — we warm their JS chunk instead.
 */
export function prefetchRoute(qc: QueryClient, path: string): void {
  void ROUTE_CHUNK[path]?.();
  switch (path) {
    case '/projects':
      void qc.prefetchQuery(projectsListOptions());
      break;
    case '/dispatch':
      void qc.prefetchQuery(dispatchDayOptions(todayLocalISODate()));
      break;
    case '/schedule':
      void qc.prefetchQuery(scheduleWeekOptions(mondayOfWeek(), 7));
      break;
    case '/activity':
      void qc.prefetchQuery(activityFeedOptions(20));
      break;
    case '/crew':
      void qc.prefetchQuery(crewOptions());
      break;
    case '/today':
      void qc.prefetchQuery(crewTodayOptions());
      break;
    case '/my-week':
      void qc.prefetchQuery(crewScheduleOptions());
      break;
    default:
      break;
  }
}

/** Warm a single project's detail — the highest-traffic drill-down (row hover). */
export function prefetchProject(qc: QueryClient, id: string): void {
  void qc.prefetchQuery(projectDetailOptions(id));
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

/**
 * Run a low-priority callback when the browser is idle. Uses requestIdleCallback
 * where available (so prefetch never competes with first paint or interaction),
 * with a short-timeout fallback for browsers without it. Returns a canceller.
 */
export function onIdle(cb: () => void): () => void {
  const w = window as IdleWindow;
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(cb, { timeout: 2_000 });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(cb, 200);
  return () => window.clearTimeout(id);
}
